import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ChessBoard from './ChessBoard';
import MobileChessBoard from './MobileChessBoard';
import MobileGameLayout from './MobileGameLayout';
import MobileGameInfo from './MobileGameInfo';
import MobileTimer from './MobileTimer';
import GameInfo from './GameInfo';
import Timer from './Timer';
import GameOverModal from './GameOverModal';
import useDeviceDetection from '../hooks/useDeviceDetection';
import { initializeBoard, isValidMove, makeMove, isInCheck, isCheckmate, isStalemate } from '../utils/chessLogic';
import { getComputerMove } from '../utils/computerPlayer';
import { recordGameResult } from '../utils/leaderboardManager';

const ChessGame = () => {
  const { mode } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useDeviceDetection();
  
  const [board, setBoard] = useState(initializeBoard());
  const [currentPlayer, setCurrentPlayer] = useState('white');
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  const [gameStatus, setGameStatus] = useState('playing');
  const [winner, setWinner] = useState(null);
  const [moveHistory, setMoveHistory] = useState([]);
  const [capturedPieces, setCapturedPieces] = useState({ white: [], black: [] });
  const [isComputerThinking, setIsComputerThinking] = useState(false);
  const [gameStartTime] = useState(Date.now());
  const [gameStats, setGameStats] = useState({
    totalMoves: 0,
    checksDelivered: 0,
    checkmatesDelivered: 0,
    piecesCaptures: 0
  });

  const currentPlayerName = localStorage.getItem('chess_player_name') || 'Player';

  const resetTimer = useCallback(() => {
    // Timer will be reset by the Timer component when currentPlayer changes
  }, []);

  const handleTimeUp = useCallback(() => {
    const winner = currentPlayer === 'white' ? 'black' : 'white';
    setGameStatus('timeout');
    setWinner(winner);
  }, [currentPlayer]);

  // Record game result when game ends
  useEffect(() => {
    if (gameStatus === 'checkmate' || gameStatus === 'stalemate' || gameStatus === 'timeout') {
      const gameDuration = Date.now() - gameStartTime;
      let result, endReason;

      if (gameStatus === 'checkmate') {
        result = winner === 'white' ? 'win' : 'loss';
        endReason = 'checkmate';
        setGameStats(prev => ({
          ...prev,
          checkmatesDelivered: prev.checkmatesDelivered + (result === 'win' ? 1 : 0)
        }));
      } else if (gameStatus === 'stalemate') {
        result = 'draw';
        endReason = 'stalemate';
      } else if (gameStatus === 'timeout') {
        result = winner === 'white' ? 'win' : 'loss';
        endReason = 'timeout';
      }

      if (mode === 'computer' || mode === 'two-player') {
        recordGameResult({
          playerName: currentPlayerName,
          opponent: mode === 'computer' ? 'Computer' : 'Player 2',
          result,
          gameMode: mode,
          gameDuration,
          totalMoves: gameStats.totalMoves,
          checksDelivered: gameStats.checksDelivered,
          checkmatesDelivered: gameStats.checkmatesDelivered,
          piecesCaptures: gameStats.piecesCaptures,
          endReason
        });
      }
    }
  }, [gameStatus, winner, gameStartTime, gameStats, mode, currentPlayerName]);

  const handleSquareClick = useCallback((row, col) => {
    if (gameStatus !== 'playing' && gameStatus !== 'check') return;
    if (mode === 'computer' && currentPlayer === 'black') return;
    if (isComputerThinking) return;

    const piece = board[row][col];

    if (selectedSquare) {
      const [selectedRow, selectedCol] = selectedSquare;

      if (selectedRow === row && selectedCol === col) {
        setSelectedSquare(null);
        setValidMoves([]);
        return;
      }

      if (validMoves.some(move => move.row === row && move.col === col)) {
        const moveResult = makeMove(board, selectedRow, selectedCol, row, col, currentPlayer);
        if (moveResult.success) {
          const newBoard = moveResult.newBoard;
          const capturedPiece = moveResult.capturedPiece;

          setBoard(newBoard);
          setMoveHistory(prev => [...prev, {
            from: [selectedRow, selectedCol],
            to: [row, col],
            piece: board[selectedRow][selectedCol],
            captured: capturedPiece
          }]);

          setGameStats(prev => ({
            ...prev,
            totalMoves: prev.totalMoves + 1,
            piecesCaptures: prev.piecesCaptures + (capturedPiece ? 1 : 0)
          }));

          if (capturedPiece) {
            setCapturedPieces(prev => ({
              ...prev,
              [currentPlayer]: [...prev[currentPlayer], capturedPiece]
            }));
          }

          const nextPlayer = currentPlayer === 'white' ? 'black' : 'white';

          if (isCheckmate(newBoard, nextPlayer)) {
            setGameStatus('checkmate');
            setWinner(currentPlayer);
          } else if (isStalemate(newBoard, nextPlayer)) {
            setGameStatus('stalemate');
          } else if (isInCheck(newBoard, nextPlayer)) {
            setGameStatus('check');
            setCurrentPlayer(nextPlayer);
            setGameStats(prev => ({
              ...prev,
              checksDelivered: prev.checksDelivered + 1
            }));
          } else {
            setGameStatus('playing');
            setCurrentPlayer(nextPlayer);
          }
        }

        setSelectedSquare(null);
        setValidMoves([]);
      } else if (piece && piece.color === currentPlayer) {
        setSelectedSquare([row, col]);
        setValidMoves(isValidMove(board, row, col, currentPlayer));
      } else {
        setSelectedSquare(null);
        setValidMoves([]);
      }
    } else if (piece && piece.color === currentPlayer) {
      setSelectedSquare([row, col]);
      setValidMoves(isValidMove(board, row, col, currentPlayer));
    }
  }, [board, selectedSquare, validMoves, currentPlayer, gameStatus, mode, isComputerThinking]);

  // Computer move logic
  useEffect(() => {
    if (mode === 'computer' && currentPlayer === 'black' && (gameStatus === 'playing' || gameStatus === 'check')) {
      setIsComputerThinking(true);
      const timer = setTimeout(() => {
        const computerMove = getComputerMove(board, 'black');
        if (computerMove) {
          const { from, to } = computerMove;
          const moveResult = makeMove(board, from[0], from[1], to[0], to[1], 'black');
          
          if (moveResult.success) {
            const newBoard = moveResult.newBoard;
            const capturedPiece = moveResult.capturedPiece;

            setBoard(newBoard);
            setMoveHistory(prev => [...prev, {
              from,
              to,
              piece: board[from[0]][from[1]],
              captured: capturedPiece
            }]);

            setGameStats(prev => ({
              ...prev,
              totalMoves: prev.totalMoves + 1
            }));

            if (capturedPiece) {
              setCapturedPieces(prev => ({
                ...prev,
                black: [...prev.black, capturedPiece]
              }));
            }

            if (isCheckmate(newBoard, 'white')) {
              setGameStatus('checkmate');
              setWinner('black');
            } else if (isStalemate(newBoard, 'white')) {
              setGameStatus('stalemate');
            } else if (isInCheck(newBoard, 'white')) {
              setGameStatus('check');
              setCurrentPlayer('white');
            } else {
              setGameStatus('playing');
              setCurrentPlayer('white');
            }
          }
        }
        setIsComputerThinking(false);
      }, 1000 + Math.random() * 1000);

      return () => clearTimeout(timer);
    }
  }, [mode, currentPlayer, gameStatus, board]);

  const resetGame = () => {
    setBoard(initializeBoard());
    setCurrentPlayer('white');
    setSelectedSquare(null);
    setValidMoves([]);
    setGameStatus('playing');
    setWinner(null);
    setMoveHistory([]);
    setCapturedPieces({ white: [], black: [] });
    setIsComputerThinking(false);
    setGameStats({
      totalMoves: 0,
      checksDelivered: 0,
      checkmatesDelivered: 0,
      piecesCaptures: 0
    });
  };

  const goToMenu = () => {
    navigate('/');
  };

  // Mobile Layout
  if (isMobile) {
    return (
      <>
        <MobileGameLayout
          mode={mode}
          currentPlayer={currentPlayer}
          gameStatus={gameStatus}
          gameStats={gameStats}
          onReset={resetGame}
          onGoToMenu={goToMenu}
          gameInfo={
            <MobileGameInfo
              mode={mode}
              moveHistory={moveHistory}
              capturedPieces={capturedPieces}
              isComputerThinking={isComputerThinking}
              gameStats={gameStats}
            />
          }
          timer={
            <MobileTimer
              currentPlayer={currentPlayer}
              gameStatus={gameStatus}
              onTimeUp={handleTimeUp}
              key={`${currentPlayer}-${moveHistory.length}`}
            />
          }
        >
          <MobileChessBoard
            board={board}
            selectedSquare={selectedSquare}
            validMoves={validMoves}
            onSquareClick={handleSquareClick}
            currentPlayer={currentPlayer}
          />
        </MobileGameLayout>

        <GameOverModal
          isOpen={gameStatus === 'checkmate' || gameStatus === 'stalemate' || gameStatus === 'timeout'}
          gameStatus={gameStatus}
          winner={winner}
          onPlayAgain={resetGame}
          onGoToMenu={goToMenu}
        />
      </>
    );
  }

  // Desktop Layout
  return (
    <div className="min-h-screen p-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid lg:grid-cols-4 gap-6"
        >
          {/* Game Info - Left Side */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <GameInfo
              mode={mode}
              currentPlayer={currentPlayer}
              gameStatus={gameStatus}
              moveHistory={moveHistory}
              capturedPieces={capturedPieces}
              isComputerThinking={isComputerThinking}
              onReset={resetGame}
              onGoToMenu={goToMenu}
            />
          </div>

          {/* Chess Board - Center */}
          <div className="lg:col-span-2 order-1 lg:order-2 flex flex-col items-center">
            <Timer
              currentPlayer={currentPlayer}
              gameStatus={gameStatus}
              onTimeUp={handleTimeUp}
              key={`${currentPlayer}-${moveHistory.length}`}
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mt-4"
            >
              <ChessBoard
                board={board}
                selectedSquare={selectedSquare}
                validMoves={validMoves}
                onSquareClick={handleSquareClick}
                currentPlayer={currentPlayer}
              />
            </motion.div>
          </div>

          {/* Timer and Status - Right Side */}
          <div className="lg:col-span-1 order-3">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-4">Game Status</h3>
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-gray-300 mb-2">Current Turn</p>
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${
                    currentPlayer === 'white' 
                      ? 'bg-white text-black' 
                      : 'bg-gray-800 text-white'
                  }`}>
                    <span className="text-2xl">
                      {currentPlayer === 'white' ? '♔' : '♛'}
                    </span>
                    <span className="font-semibold capitalize">
                      {currentPlayer}
                    </span>
                  </div>
                </div>

                {gameStatus === 'check' && (
                  <motion.div
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    className="text-center p-3 bg-red-500/20 border border-red-500/50 rounded-lg"
                  >
                    <p className="text-red-300 font-semibold">CHECK!</p>
                  </motion.div>
                )}

                {isComputerThinking && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center p-3 bg-blue-500/20 border border-blue-500/50 rounded-lg"
                  >
                    <p className="text-blue-300">Computer is thinking...</p>
                    <div className="flex justify-center mt-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-300"></div>
                    </div>
                  </motion.div>
                )}

                {/* Game Stats */}
                <div className="border-t border-white/10 pt-4">
                  <h4 className="text-white font-semibold mb-2">Game Stats</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Moves:</span>
                      <span className="text-white">{gameStats.totalMoves}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Checks:</span>
                      <span className="text-yellow-400">{gameStats.checksDelivered}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Captures:</span>
                      <span className="text-purple-400">{gameStats.piecesCaptures}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <GameOverModal
        isOpen={gameStatus === 'checkmate' || gameStatus === 'stalemate' || gameStatus === 'timeout'}
        gameStatus={gameStatus}
        winner={winner}
        onPlayAgain={resetGame}
        onGoToMenu={goToMenu}
      />
    </div>
  );
};

export default ChessGame;